import React, { useState, useRef, useEffect } from "react";
import theme from "../../../styles/theme";
import {
  formatDateForDisplay,
  formatDateAmericanForDisplay,
  formatDateForStorage,
  formatDateAmericanForStorage,
} from "../../../utils/dateFormat";

// ============================================================================
// DATE PICKER COMPOSANT
// ============================================================================
const CardDatePicker = ({
  label,
  value,
  onChange = () => {},

  placeholder = "",
  disabled = false,
  readOnlyMode = false,

  // Format contrôlé par settings utilisateur
  format = "EU", // "EU" = DD/MM/YYYY (par défaut), "US" = MM/DD/YYYY

  width,
  height = 36,
  labelWidth = 140,
  gap = 6,
  marginTop = 0,
  marginBottom = 0,
  padding = 0,

  style = {},
  inputStyle = {},
}) => {
  const isReadOnly = readOnlyMode || disabled;

  // POPUP calendrier
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // ---------------------------------------------------------------------------
  // Format d’affichage selon format EU / US
  // ---------------------------------------------------------------------------
  const displayValue =
    format === "EU"
      ? formatDateForDisplay(value)
      : formatDateAmericanForDisplay(value);

  // ---------------------------------------------------------------------------
  // Mise à jour saisie utilisateur
  // ---------------------------------------------------------------------------
  const handleChange = (e) => {
    const raw = e.target.value;

    const iso =
      format === "EU"
        ? formatDateForStorage(raw)
        : formatDateAmericanForStorage(raw);

    onChange(iso);
  };

  // ---------------------------------------------------------------------------
  // Fermeture clic extérieur
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /// ---------------------------------------------------------------------------
// Navigation mois dans popup
// ---------------------------------------------------------------------------

// Date courante pour le calendrier popup
const [currentDate, setCurrentDate] = useState(() => {
  if (!value) return new Date();

  const d = new Date(value); // value est en ISO (YYYY-MM-DD)
  return isNaN(d.getTime()) ? new Date() : d;
});

useEffect(() => {
  setInputValue(
    format === "EU"
      ? formatDateForDisplay(value)
      : formatDateAmericanForDisplay(value)
  );
}, [value, format]);


const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();

const selectDate = (day) => {
  const y = currentDate.getFullYear();
  const m = currentDate.getMonth();
  const d = day;

  // Toujours ISO → indépendant du format EU/US
  const iso = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(
    2,
    "0"
  )}`;

  onChange(iso);
  setOpen(false);
};

const goPrevMonth = () => {
  const d = new Date(currentDate);
  d.setMonth(d.getMonth() - 1);
  setCurrentDate(d);
};

const goNextMonth = () => {
  const d = new Date(currentDate);
  d.setMonth(d.getMonth() + 1);
  setCurrentDate(d);
};

const y = currentDate.getFullYear();
const m = currentDate.getMonth();
const nbDays = daysInMonth(y, m);


// Validation légère : empêche caractères non numériques
const handleInputKeyDown = (e) => {
  const allowed =
    /[0-9]/.test(e.key) ||
    ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key) ||
    (e.key === "/" && !e.target.value.endsWith("/"));

  if (!allowed) {
    e.preventDefault();
  }
};

// Auto-formatage progressif EU / US
const handleInputChange = (e) => {
  let v = e.target.value;

  v = v.replace(/\/{2,}/g, "/");
  v = v.replace(/[^\d/]/g, "");

  if (format === "EU") {
    if (/^\d{2}$/.test(v)) v += "/";
    if (/^\d{2}\/\d{2}$/.test(v)) v += "/";
  } else {
    if (/^\d{2}$/.test(v)) v += "/";
    if (/^\d{2}\/\d{2}$/.test(v)) v += "/";
  }

  if (v.length > 10) v = v.slice(0, 10);

  // Mise à jour locale pour que l'utilisateur voie ce qu'il tape
  setInputValue(v);

  const full = /^\d{2}\/\d{2}\/\d{4}$/;

  if (full.test(v)) {
    const iso =
      format === "EU"
        ? formatDateForStorage(v)
        : formatDateAmericanForStorage(v);

    onChange(iso);
  } else {
    onChange("");
  }
};





const [inputValue, setInputValue] = useState(displayValue);








  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div
      ref={wrapperRef}
      style={{
        display: "flex",
        alignItems: "center",
        width,
        gap,
        marginTop,
        marginBottom,
        padding,
        position: "relative",
        ...style,
      }}
    >
      {/* LABEL */}
      {label && (
        <label
          style={{
            width: labelWidth,
            minWidth: labelWidth,
            fontFamily: theme.form.label.fontFamily,
            fontSize: theme.form.label.fontSize,
            fontWeight: theme.form.label.fontWeight,
            color: theme.form.label.color,
          }}
        >
          {label}
        </label>
      )}

      {/* CHAMP INPUT + ICONE */}
      <div
        style={{
          flex: 1,
          position: "relative",
          display: "flex",
          alignItems: "center",
        }}
      >
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}


          disabled={isReadOnly}
          style={{
            flex: 1,
            height,
            fontFamily: theme.form.field.fontFamily,
            fontSize: theme.form.field.fontSize,
            fontWeight: theme.form.field.fontWeight,
            color: theme.form.field.color,
            borderRadius: isReadOnly ? 0 : theme.borders.radius.md,
            border: isReadOnly
              ? "none"
              : `1px solid ${theme.colors.borders.default}`,
            backgroundColor: isReadOnly
              ? "transparent"
              : theme.colors.background.input,
            outline: "none",
            appearance: "none",
            WebkitAppearance: "none",
            MozAppearance: "none",
            transition: "border-color 0.2s ease",
            ...inputStyle,
          }}
          onFocus={(e) =>
            !isReadOnly &&
            (e.target.style.borderColor = theme.colors.borders.focus)
          }
          onBlur={(e) =>
            !isReadOnly &&
            (e.target.style.borderColor = theme.colors.borders.default)
          }
        />


        {/* ICON CALENDRIER */}
        <img
          src="/assets/icones/container/calendar.svg"
          alt="calendar"
          onClick={() => !isReadOnly && setOpen((v) => !v)}
          style={{
            position: "absolute",
            right: 8,
            width: 16,
            height: 16,
            opacity: 0.6,
            cursor: isReadOnly ? "default" : "pointer",
          }}
        />
      </div>

      {/* POPUP CALENDRIER */}
      {open && !isReadOnly && (
        <div
          style={{
            position: "absolute",
            top: height + 6,
            left: labelWidth + gap,
            zIndex: 50,
            background: "#fff",
            border: `1px solid ${theme.colors.borders.default}`,
            borderRadius: theme.borders.radius.md,
            padding: 10,
            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          }}
        >
          {/* Header navigation */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 8,
              fontSize: 13,
            }}
          >
            <span
              style={{ cursor: "pointer", padding: "0 4px" }}
              onClick={goPrevMonth}
            >
              ‹
            </span>
            <strong>
              {currentDate.toLocaleString("fr-FR", {
                month: "long",
                year: "numeric",
              })}
            </strong>
            <span
              style={{ cursor: "pointer", padding: "0 4px" }}
              onClick={goNextMonth}
            >
              ›
            </span>
          </div>

          {/* Jours */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 28px)",
              gap: 4,
              textAlign: "center",
              fontSize: 12,
              marginBottom: 4,
            }}
          >
            {["L", "M", "M", "J", "V", "S", "D"].map((d) => (
              <div key={d} style={{ opacity: 0.6 }}>
                {d}
              </div>
            ))}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 28px)",
              gap: 4,
              textAlign: "center",
            }}
          >
            {Array.from({ length: nbDays }).map((_, i) => {
              const day = i + 1;
              return (
                <div
                  key={day}
                  onClick={() => selectDate(day)}
                  style={{
                    padding: "4px 0",
                    borderRadius: 4,
                    cursor: "pointer",
                    background:
                      displayValue.startsWith(
                        String(day).padStart(2, "0")
                      ) && open
                        ? theme.colors.background.hover
                        : "transparent",
                  }}
                >
                  {day}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CardDatePicker;
